import type { ScalarField } from '../sim/grid.ts';

/**
 * Colour ramps shared by the avalanche deposit overlays (`overlayColors.ts`) and the particle
 * playback (`avalancheParticles.ts`), so a run's speed/thickness/pressure read the same whether
 * you're looking at the terrain overlay or the particles themselves.
 */

export type RGB = readonly [number, number, number];

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function mix(a: RGB, b: RGB, t: number): RGB {
  const c = clamp01(t);
  return [lerp(a[0], b[0], c), lerp(a[1], b[1], c), lerp(a[2], b[2], c)];
}

function sequentialColor(value: number, max: number, stops: RGB[]): RGB {
  const steps = stops.length - 1;
  const t = clamp01(max > 0 ? value / max : 0) * steps;
  const i0 = Math.min(steps - 1, Math.floor(t));
  return mix(stops[i0]!, stops[i0 + 1]!, t - i0);
}

const WHITE: RGB = [1, 1, 1];

function divergingColor(value: number, half: number, neg: RGB, pos: RGB): RGB {
  const t = clamp01(Math.abs(value) / Math.max(1e-9, half));
  return value >= 0 ? mix(WHITE, pos, t) : mix(WHITE, neg, t);
}

function rampMax(field: ScalarField, floor: number): number {
  let max = 0;
  for (const v of field.data) if (!Number.isNaN(v) && v > max) max = v;
  return Math.max(floor, max);
}

/**
 * Symmetric range for a diverging ramp: the 98th percentile of |value| over the cells an
 * avalanche actually touched. Untouched cells (exactly 0) are excluded — they are nearly all of
 * the grid, and including them collapsed the range to its floor, saturating every real change.
 */
function halfRange(field: ScalarField, floor: number): number {
  const abs: number[] = [];
  for (const v of field.data) if (!Number.isNaN(v) && v !== 0) abs.push(Math.abs(v));
  abs.sort((a, b) => a - b);
  const p98 = abs.length > 0 ? abs[Math.floor(0.98 * (abs.length - 1))]! : 0;
  return Math.max(floor, p98);
}

/** Sequential deposit-depth ramp: grey (none) through amber to dark brown. */
const DEPOSIT_STOPS: RGB[] = [
  [0.62, 0.62, 0.62],
  [0.92, 0.78, 0.45],
  [0.72, 0.42, 0.12],
  [0.32, 0.16, 0.05],
];
export function depositRampMax(field: ScalarField): number {
  return rampMax(field, 0.2);
}
export function depositColor(depthM: number, max: number): RGB {
  return sequentialColor(Math.max(0, depthM), max, DEPOSIT_STOPS);
}

/** Diverging net-change colours: red/orange = removed, blue = deposited. */
const NET_REMOVED: RGB = [0.85, 0.25, 0.1];
const NET_DEPOSIT: RGB = [0.15, 0.4, 0.85];
export function netHalfRange(field: ScalarField): number {
  return halfRange(field, 0.05);
}
export function netColor(value: number, half: number): RGB {
  return divergingColor(value, half, NET_REMOVED, NET_DEPOSIT);
}

/** Origin share (0 = all release, 1 = all entrained). */
const ORIGIN_STOPS: RGB[] = [
  [0.3, 0.5, 0.85],
  [0.55, 0.4, 0.75],
  [0.85, 0.3, 0.35],
];
export function originColor(share: number): RGB {
  return sequentialColor(share, 1, ORIGIN_STOPS);
}
export const ORIGIN_RELEASED_COLOR: RGB = ORIGIN_STOPS[0]!;
export const ORIGIN_ENTRAINED_COLOR: RGB = ORIGIN_STOPS[2]!;

/** Speed ramp (m/s), a fixed 0–40 m/s domain so the legend and particle colours agree run to
 * run (rather than rescaling to each run's own peak). */
export const SPEED_MAX_REF = 40;
const SPEED_STOPS: RGB[] = [
  [0.2, 0.35, 0.75],
  [0.25, 0.7, 0.55],
  [0.95, 0.8, 0.2],
  [0.85, 0.15, 0.1],
];
export function speedColor(speedMs: number, max: number = SPEED_MAX_REF): RGB {
  return sequentialColor(speedMs, max, SPEED_STOPS);
}
export function speedRampMax(field: ScalarField): number {
  return rampMax(field, 5);
}

/** Flow/deposit-thickness ramp (m), a fixed 0–3 m domain. */
export const THICKNESS_MAX_REF = 3;
const THICKNESS_STOPS: RGB[] = [
  [0.75, 0.8, 0.85],
  [0.35, 0.55, 0.8],
  [0.2, 0.25, 0.55],
  [0.1, 0.08, 0.25],
];
export function thicknessColor(hM: number, max: number = THICKNESS_MAX_REF): RGB {
  return sequentialColor(hM, max, THICKNESS_STOPS);
}
export function thicknessRampMax(field: ScalarField): number {
  return rampMax(field, 0.2);
}

/** Impact-pressure ramp (kPa), piecewise so the visually distinct bands land on the reference
 * thresholds: ≈3 kPa can break windows, ≈30 kPa can destroy timber-frame houses, ≈100 kPa
 * flattens mature forest. */
export const PRESSURE_REF_TICKS = { windows: 3, houses: 30, forest: 100 } as const;
const PRESSURE_BANDS: { max: number; from: RGB; to: RGB }[] = [
  { max: 3, from: [0.75, 0.8, 0.85], to: [0.9, 0.85, 0.3] },
  { max: 30, from: [0.9, 0.85, 0.3], to: [0.9, 0.45, 0.1] },
  { max: 100, from: [0.9, 0.45, 0.1], to: [0.75, 0.1, 0.1] },
];
const PRESSURE_MAX_COLOR: RGB = [0.25, 0.02, 0.05];
export function pressureColor(kPa: number): RGB {
  if (kPa <= 0) return PRESSURE_BANDS[0]!.from;
  let lo = 0;
  for (const band of PRESSURE_BANDS) {
    if (kPa <= band.max) return mix(band.from, band.to, (kPa - lo) / (band.max - lo));
    lo = band.max;
  }
  const last = PRESSURE_BANDS[PRESSURE_BANDS.length - 1]!;
  return mix(last.to, PRESSURE_MAX_COLOR, clamp01((kPa - lo) / lo));
}

/** Moving vs. stopped particle colours. */
export const MOVING_COLOR: RGB = [0.95, 0.55, 0.15];
export const STOPPED_COLOR: RGB = [0.55, 0.58, 0.62];
